import { useState, useEffect, type SyntheticEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Heading, Text } from '@radix-ui/themes';
import './Schedule.css';
import '../../fonts.css';
import { toast } from 'react-toastify';
import config from '../../config';
import type { Event, Venue } from '../../types';
import { buildEventSchema } from '../../utils/structuredData';
import { useSiteConfig } from '../../utils/siteConfig';

const MAX_EVENTS = 8;

interface EventWithCalculatedDate extends Event {
  calculatedDate: Date;
  venue: Venue;
}

const Schedule: React.FC = () => {
  const [events, setEvents] = useState<EventWithCalculatedDate[]>([]);
  const [loading, setLoading] = useState(true);
  const { get: getConfig } = useSiteConfig();

  useEffect(() => {
    fetchUpcomingEvents();
  }, []);

  const fetchUpcomingEvents = async () => {
    try {
      const response = await fetch(`${config.apiBaseUrl}/events/upcoming`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const allEvents: (Event & { venue?: Venue })[] = await response.json();

      const expanded: EventWithCalculatedDate[] = [];

      for (const e of allEvents) {
        if (!e.venue) continue;
        const ev = e as Event & { venue: Venue };

        if (ev.type === 'recurring' && !ev.date) {
          // Expand recurring events into multiple occurrences
          const weeks = ev.recurringShowWeeks ?? 1;
          const occurrences = calculateOccurrences(ev, weeks);
          for (const date of occurrences) {
            expanded.push({ ...ev, calculatedDate: date });
          }
        } else {
          expanded.push({ ...ev, calculatedDate: new Date(ev.date ?? new Date()) });
        }
      }

      const processed = expanded
        .sort((a, b) => a.calculatedDate.getTime() - b.calculatedDate.getTime())
        .slice(0, MAX_EVENTS);

      setEvents(processed);
    } catch (err) {
      console.error('Error fetching events:', err);
      toast.error('Unable to load upcoming events');
    } finally {
      setLoading(false);
    }
  };

  const calculateOccurrences = (event: Event, weeks: number): Date[] => {
    const now = new Date();
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    let matchingDay = event.recurringDay;
    if (!matchingDay) {
      matchingDay = days.find((d) => event.name.toLowerCase().includes(d)) ?? null;
    }
    if (!matchingDay) return [now];

    const eventDay = days.indexOf(matchingDay.toLowerCase());
    let diff = eventDay - now.getDay();
    if (diff < 0) diff += 7;

    const first = new Date(now);
    first.setDate(now.getDate() + diff);
    if (event.recurringTime) {
      const [h, m] = event.recurringTime.split(':').map(Number);
      first.setHours(h ?? 19, m ?? 30, 0, 0);
    } else {
      first.setHours(19, 30, 0, 0);
    }

    // If today's occurrence already passed, start from next week
    if (first <= now) {
      first.setDate(first.getDate() + 7);
    }

    const results: Date[] = [];
    for (let i = 0; i < weeks; i++) {
      const d = new Date(first);
      d.setDate(first.getDate() + i * 7);
      results.push(d);
    }
    return results;
  };

  const formatDateTime = (date: Date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return { day: 'TBD', date: 'TBD', time: 'TBD' };
    }
    return {
      day: date.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'America/Denver' }),
      date: date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        timeZone: 'America/Denver',
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
        timeZone: 'America/Denver',
      }),
    };
  };

  const handleImageError = (e: SyntheticEvent<HTMLImageElement>) => {
    (e.target as HTMLImageElement).style.display = 'none';
  };

  if (loading) {
    return (
      <div className="upcoming-dates-container">
        <Heading as="h2" size={{ initial: '6', md: '7' }} className="title">Upcoming Dates</Heading>
        <Text color="gray" className="loading">Loading events…</Text>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="upcoming-dates-container">
        <Heading as="h2" size={{ initial: '6', md: '7' }} className="title">Upcoming Dates</Heading>
        <Text color="gray" className="no-events">No upcoming events scheduled</Text>
      </div>
    );
  }

  return (
    <div className="upcoming-dates-container">
      <Helmet>
        {events.map((event, idx) => (
          <script key={`${event.id}-${idx}`} type="application/ld+json">
            {JSON.stringify(buildEventSchema(event, getConfig('brandName', 'West Slope Quiz')))}
          </script>
        ))}
      </Helmet>
      <Heading as="h2" size={{ initial: '6', md: '7' }} className="title">Upcoming Dates</Heading>
      <ul className="dates-list">
        {events.map((event, idx) => {
          const { day, date, time } = formatDateTime(event.calculatedDate);
          return (
            <li key={`${event.id}-${idx}`} className="date-item">
              <div className="business-info">
                <a
                  href={event.venue.link || '#'}
                  target="_blank"
                  rel="noreferrer"
                  className={`venue-link${!event.venue.link ? ' no-link' : ''}`}
                >
                  {event.venue.logo && (
                    <img
                      src={event.venue.logo}
                      alt={`${event.venue.name} logo`}
                      className="logo"
                      width={250}
                      height={100}
                      loading="lazy"
                      decoding="async"
                      onError={handleImageError}
                    />
                  )}
                  <span className="business-name">{event.venue.name ?? 'Unknown Venue'}</span>
                </a>
                {event.name && (
                  <Link to={`/events/${event.id}`} className="event-name-link">
                    <span className="event-name">{event.name}</span>
                  </Link>
                )}
              </div>
              <div className="date-item-labels">
                <span className="day">{day}</span>
                <span className="date-sep" aria-hidden="true">·</span>
                <span className="date">{date}</span>
                <span className="date-sep" aria-hidden="true">·</span>
                <span className="time">{time}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

Schedule.displayName = 'Schedule';

export default Schedule;
